/**
 * InViewReveal — viewport-triggered entrance wrapper.
 *
 * Wraps an arbitrary child and plays a soft fade + rise the first time
 * the child scrolls into view. Long manuscript-style demos reach for
 * this primitive repeatedly: every callout, theorem card, and witness
 * grid wants to animate in once and stay still afterward.
 *
 * Built on `react-intersection-observer` so the observation costs
 * scale with the number of revealed elements and not with scroll
 * frequency. Honors `prefers-reduced-motion`: the child renders
 * immediately with no animation when the user opted out.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { useInView } from "react-intersection-observer";
import { motion, useReducedMotion } from "motion/react";

export interface InViewRevealProps {
  /** Element revealed when it enters the viewport. */
  readonly children: React.ReactNode;
  /**
   * Viewport intersection fraction that triggers the reveal. Defaults
   * to `0.2` so the reveal fires once a fifth of the element is visible.
   */
  readonly threshold?: number;
  /** Animation duration, in ms. Defaults to 480. */
  readonly durationMs?: number;
  /** Pixel offset travelled by the rise. Defaults to 16. */
  readonly distance?: number;
  /**
   * Pre-roll delay before the animation starts (ms). Useful for
   * staggering siblings. Defaults to 0.
   */
  readonly delayMs?: number;
  /** Optional className passed to the motion wrapper. */
  readonly className?: string;
}

/** Reveals its child on scroll-in with a soft fade + rise. */
export const InViewReveal = React.memo(function InViewReveal({
  children,
  threshold = 0.2,
  durationMs = 480,
  distance = 16,
  delayMs = 0,
  className,
}: InViewRevealProps): React.JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const { ref, inView } = useInView({ threshold, triggerOnce: true });

  if (prefersReducedMotion) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      data-csk-in-view={inView ? "visible" : "hidden"}
      initial={{ opacity: 0, y: distance }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: distance }}
      transition={{ duration: durationMs / 1000, delay: delayMs / 1000, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
});
